import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { format } from 'date-fns';
import { CopyIcon } from 'lucide-react';
import { overlay } from 'overlay-kit';
import { toast } from 'sonner';
import { api, getErrorMessage } from '@/shared/api/api';
import { USER_ROLE, type User, type UserRole } from '@/shared/types/user';
import {
  Button,
  Checkbox,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Text } from '@/shared/ui/text';
import { INFO_USER_ROLE_TERM } from './schemas';

export const USER_MANAGEMENT_COLUMNS: ColumnDef<User>[] = [
  {
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        aria-label="전체 선택"
        checked={
          table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && 'indeterminate')
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        aria-label="행 선택"
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'name',
    header: '이름',
  },
  {
    accessorKey: 'email',
    header: '이메일 주소',
  },
  {
    accessorKey: 'is_confirmed',
    header: '상태',
    cell: ({ row }) => {
      const { id, is_confirmed } = row.original;
      const status = is_confirmed ? 'active' : 'inactive';
      const queryClient = useQueryClient();

      const { mutate: updateUserStatus } = useMutation({
        mutationKey: ['update-user-status', id],
        mutationFn: async ({ is_confirmed }: { is_confirmed: boolean }) => {
          return await api.patch(`admin/users/${id}`, { is_confirmed });
        },
        onSuccess: () => {
          toast.success('상태가 변경되었습니다.');
        },
        onError: async (error) => {
          const errorMessage = getErrorMessage(error);
          toast.error(errorMessage);
          await queryClient.invalidateQueries({ queryKey: ['users'] });
        },
      });

      const handleUpdateUserStatus = (is_confirmed: boolean) => {
        updateUserStatus({ is_confirmed });
      };

      return (
        <Select
          defaultValue={status}
          onValueChange={(value) => handleUpdateUserStatus(value === 'active')}
        >
          <SelectTrigger
            className="bg-white w-full"
            size="sm"
          >
            <SelectValue placeholder="상태를 선택해주세요." />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">활성</SelectItem>
            <SelectItem value="inactive">비활성</SelectItem>
          </SelectContent>
        </Select>
      );
    },
  },
  {
    accessorKey: 'role',
    header: '권한',
    cell: ({ row }) => {
      const { id, role, is_confirmed } = row.original;
      const queryClient = useQueryClient();

      const { mutate: updateUserRole } = useMutation({
        mutationKey: ['update-user-role', id],
        mutationFn: async ({ role, is_confirmed }: { role: UserRole; is_confirmed: boolean }) => {
          return await api.patch(`admin/users/${id}`, { role, is_confirmed });
        },
        onSuccess: () => {
          toast.success('권한이 변경되었습니다.');
        },
        onError: async (error) => {
          const errorMessage = getErrorMessage(error);
          toast.error(errorMessage);
          await queryClient.invalidateQueries({ queryKey: ['users'] });
        },
      });

      const handleUpdateUserRole = (role: UserRole) => {
        updateUserRole({ role, is_confirmed });
      };

      return (
        <Select
          defaultValue={role}
          onValueChange={handleUpdateUserRole}
        >
          <SelectTrigger
            className="bg-white w-full"
            size="sm"
          >
            <SelectValue placeholder="권한을 선택해주세요." />
          </SelectTrigger>
          <SelectContent>
            {Object.values(USER_ROLE).map((role) => (
              <SelectItem
                key={role}
                value={role}
              >
                {INFO_USER_ROLE_TERM[role].label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    },
  },
  {
    accessorKey: 'last_login_at',
    header: '최근 접속일시',
    cell: ({ row }) => {
      const lastLoginAt = row.getValue('last_login_at');
      if (!lastLoginAt) return null;
      return (
        <Text typography="typo-text-xl">
          {format(new Date(lastLoginAt as string), 'yyyy-MM-dd HH:mm')}
        </Text>
      );
    },
  },
  {
    accessorKey: 'created_at',
    header: '가입일',
    cell: ({ row }) => (
      <Text typography="typo-text-xl">
        {format(new Date(row.getValue('created_at')), 'yyyy-MM-dd')}
      </Text>
    ),
  },
  {
    accessorKey: 'action',
    header: '',
    cell: ({ row }) => {
      const { id } = row.original;
      const queryClient = useQueryClient();

      const { mutate: resetUserPassword } = useMutation({
        mutationKey: ['reset-user-password', id],
        mutationFn: async () =>
          await api.post<{
            user_id: string;
            email: string;
            temporary_password: string;
          }>(`admin/users/${id}/reset-password`),
        onSuccess: (data) => {
          toast.success('비밀번호가 초기화되었습니다.');

          overlay.open(({ isOpen, close }) => (
            <Dialog
              onOpenChange={close}
              open={isOpen}
            >
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>비밀번호 초기화 안내</DialogTitle>
                  <DialogDescription>비밀번호가 초기화되었습니다.</DialogDescription>
                </DialogHeader>
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-md border">
                  <Text
                    className="flex-1 font-mono"
                    typography="typo-text-xl"
                  >
                    비밀번호: {data.temporary_password}
                  </Text>
                  <Button
                    onClick={async () => {
                      await navigator.clipboard.writeText(data.temporary_password);
                      toast.success('비밀번호가 클립보드에 복사되었습니다.');
                    }}
                    size="sm"
                    variant="outline"
                  >
                    <CopyIcon className="size-4 mr-1" />
                    복사
                  </Button>
                </div>
                <DialogFooter>
                  <DialogClose asChild>
                    <Button variant="primary">확인</Button>
                  </DialogClose>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          ));
        },
        onError: async (error) => {
          const errorMessage = getErrorMessage(error);
          toast.error(errorMessage);
          await queryClient.invalidateQueries({ queryKey: ['users'] });
        },
      });

      return (
        <Button
          onClick={() => resetUserPassword()}
          size="sm"
          variant="outline"
        >
          비밀번호 초기화
        </Button>
      );
    },
  },
];
